import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import { Box } from "@mui/material";

// Color schemes for different tabs
const colorSchemes = {
  views: "#FFD60A",
  tips: "#00D9FF",
  streams: "#4CAF50",
  follow: "#FF5722"
};

const BarGraphRecharts = ({ graph, tab, selectedKey }) => {
  // Transform data for Recharts format
  const transformData = () => {
    if (!graph) return [];
    
    // Handle both graph.data and direct array
    const dataPoints = Array.isArray(graph) ? graph : (graph.data || []);
    
    return dataPoints.map((point) => ({
      name: point.x || point.name || point.category,
      value: point.y || point.value || 0
    }));
  };

  const data = transformData();
  const barColor = colorSchemes[tab] || colorSchemes.views;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
          }}
        >
          <p style={{ color: "#fff", margin: 0 }}>{label}</p>
          <p style={{ color: barColor, margin: "5px 0 0 0" }}>
            {`${tab.charAt(0).toUpperCase() + tab.slice(1)}: ${payload[0].value.toLocaleString()}`}
          </p>
        </Box>
      );
    }
    return null;
  };

  // Y-axis formatter
  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  // Custom label for bars
  const renderCustomLabel = (props) => {
    const { x, y, width, height, value } = props;
    if (value === 0) return null;
    
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill="#888"
        textAnchor="middle"
        fontSize="12"
      >
        {formatYAxis(value)}
      </text>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "400px", backgroundColor: "#090909", p: 2 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis 
            dataKey="name" 
            stroke="#888"
            tick={{ fill: '#888' }}
            tickLine={{ stroke: '#333' }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis 
            stroke="#888"
            tick={{ fill: '#888' }}
            tickLine={{ stroke: '#333' }}
            tickFormatter={formatYAxis}
          />
          <Tooltip 
            content={<CustomTooltip />}
            cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
          />
          <Legend 
            wrapperStyle={{ color: "#888" }}
            iconType="rect"
          />
          <Bar
            dataKey="value"
            name={tab.charAt(0).toUpperCase() + tab.slice(1)}
            fill={barColor}
            label={renderCustomLabel}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default BarGraphRecharts;